import React, { useCallback, useState } from 'react';

import { A11YLiveMessage, type A11YLiveMessageProps } from '../components/A11YLiveMessage';

export interface UseLiveAnnouncerOptions {
    /** Props forwarded to the live region element(s). */
    liveMessageProps?: A11YLiveMessageProps;
    /**
     * When `true`, maintains two live regions and alternates which one receives the message,
     * so each announcement always lands in a region that was previously empty. This ensures
     * repeated identical messages are announced even when screen readers suppress duplicates
     * from the same region. Enable this when announcement messages may repeat verbatim.
     * Defaults to `false`.
     */
    enableDoubleRegion?: boolean;
}

/**
 * Hook that provides a reliable way to announce messages to screen readers,
 * including repeated announcements of the same message.
 *
 * By default, uses a single live region. Set `enableDoubleRegion` to use two alternating
 * regions instead — this ensures repeated identical messages are always announced, since
 * each announcement lands in a region that was previously empty.
 *
 * @param liveMessageProps - Props forwarded to the live region element(s).
 * @param enableDoubleRegion - Set to `true` to use two alternating live regions.
 * @returns `announce` — call with a message string to trigger an announcement.
 * @returns `node` — render this in the component tree. Must always be present in the DOM;
 * never conditionally render it, as `aria-live` regions must exist before content is added.
 */
export const useLiveAnnouncer = ({ liveMessageProps, enableDoubleRegion = false }: UseLiveAnnouncerOptions = {}) => {
    const [, setSlot] = useState<0 | 1>(0);
    const [messages, setMessages] = useState<[string, string]>(['', '']);
    const [message, setMessage] = useState('');

    const announce = useCallback(
        (msg: string) => {
            if (enableDoubleRegion) {
                setSlot((prev) => {
                    const next = (prev === 0 ? 1 : 0) as 0 | 1;
                    setMessages(
                        (['', ''] as [string, string]).map((_, i) => (i === next ? msg : '')) as [string, string],
                    );
                    return next;
                });
            } else {
                setMessage(msg);
            }
        },
        [enableDoubleRegion],
    );

    const node = enableDoubleRegion ? (
        <>
            <A11YLiveMessage role="status" {...liveMessageProps}>
                {messages[0]}
            </A11YLiveMessage>
            <A11YLiveMessage role="status" {...liveMessageProps}>
                {messages[1]}
            </A11YLiveMessage>
        </>
    ) : (
        <A11YLiveMessage role="status" {...liveMessageProps}>
            {message}
        </A11YLiveMessage>
    );

    return { announce, node };
};
